import { useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { Button } from '@/components/ui/button';
import { FieldDescription, FieldError, FieldLabel, fieldIds } from './shared';
import type { FieldProps } from './FieldProps';

/**
 * Captures a signature as a base64 PNG data URL — the field's value IS the
 * image, no upload step. Simple enough for v1 that it needed no new
 * dependency; storing a real photo this way (base64 inline in
 * `FormSubmission.Data`) would be a different call — see `PhotoField`, which
 * uploads instead.
 *
 * Pointer Events, not HTML5 drag-and-drop or mouse-only handlers — the same
 * reasoning the dispatch board's drag gesture documents (web/CONVENTIONS.md,
 * M1-11): one code path for mouse, trackpad, and a finger on a phone screen,
 * which is exactly how this field is signed in practice. `touch-action: none`
 * stops the browser claiming the gesture for scrolling before a stroke starts.
 */
export function SignatureField({ field, value, error, onChange }: FieldProps) {
  const { t } = useTranslation();
  const { inputId, descriptionId, errorId } = fieldIds(field.path);
  const canvasRef = useRef<HTMLCanvasElement | null>(null);
  const drawingRef = useRef(false);

  // Paint the existing value back onto the canvas — reopening a partially
  // filled form must show what was already signed, not a blank pad.
  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) {
      return;
    }
    const context = canvas.getContext('2d');
    if (!context) {
      return;
    }
    context.clearRect(0, 0, canvas.width, canvas.height);
    if (typeof value === 'string' && value) {
      const image = new Image();
      image.onload = () => context.drawImage(image, 0, 0, canvas.width, canvas.height);
      image.src = value;
    }
  }, [value]);

  function pointerPosition(event: React.PointerEvent<HTMLCanvasElement>) {
    const canvas = event.currentTarget;
    const rect = canvas.getBoundingClientRect();
    // The canvas is styled to the width of its container (`w-full`, so it
    // fits a phone screen — AC) but keeps a fixed INTERNAL drawing
    // resolution. Without rescaling, a pointer position read from the
    // CSS-rendered rect would draw in the wrong place the moment the two
    // sizes diverge, which they do on anything narrower than 400px.
    const scaleX = canvas.width / rect.width;
    const scaleY = canvas.height / rect.height;
    return { x: (event.clientX - rect.left) * scaleX, y: (event.clientY - rect.top) * scaleY };
  }

  function handlePointerDown(event: React.PointerEvent<HTMLCanvasElement>) {
    const canvas = canvasRef.current;
    const context = canvas?.getContext('2d');
    if (!canvas || !context) {
      return;
    }
    event.currentTarget.setPointerCapture(event.pointerId);
    drawingRef.current = true;
    const { x, y } = pointerPosition(event);
    context.beginPath();
    context.moveTo(x, y);
  }

  function handlePointerMove(event: React.PointerEvent<HTMLCanvasElement>) {
    if (!drawingRef.current) {
      return;
    }
    const context = canvasRef.current?.getContext('2d');
    if (!context) {
      return;
    }
    const { x, y } = pointerPosition(event);
    context.lineTo(x, y);
    context.stroke();
  }

  function handlePointerUp() {
    if (!drawingRef.current) {
      return;
    }
    drawingRef.current = false;
    const dataUrl = canvasRef.current?.toDataURL('image/png');
    if (dataUrl) {
      onChange(dataUrl);
    }
  }

  function clear() {
    const canvas = canvasRef.current;
    const context = canvas?.getContext('2d');
    if (canvas && context) {
      context.clearRect(0, 0, canvas.width, canvas.height);
    }
    onChange(undefined);
  }

  return (
    <div>
      <FieldLabel htmlFor={inputId} required={field.required}>
        {field.schema.title ?? field.key}
      </FieldLabel>
      <FieldDescription id={descriptionId} text={field.schema.description} />
      <canvas
        ref={canvasRef}
        id={inputId}
        role="img"
        aria-label={t('forms.signature.canvasLabel')}
        aria-describedby={error ? errorId : undefined}
        width={400}
        height={150}
        // Internal drawing resolution stays 400x150 (crisp regardless of
        // screen size); CSS scales it down to fit a phone screen (AC) —
        // pointerPosition() rescales pointer coordinates to match.
        style={{ aspectRatio: '400 / 150' }}
        className="w-full touch-none rounded-md border border-[var(--color-input)] bg-white"
        onPointerDown={handlePointerDown}
        onPointerMove={handlePointerMove}
        onPointerUp={handlePointerUp}
        onPointerCancel={handlePointerUp}
      />
      <div className="mt-1">
        <Button type="button" variant="outline" size="sm" onClick={clear}>
          {t('forms.signature.clear')}
        </Button>
      </div>
      <FieldError id={errorId} message={error} />
    </div>
  );
}
